/* import __COLOCATED_TEMPLATE__ from './sensitive-data-display.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { post } from 'embercom/lib/ajax';

interface Args {
  secret: string;
  secretType: 'client_secret' | 'access_token' | 'webhook_secret';
  appPackageId?: string;
  label?: string;
}

export default class SensitiveDataDisplay extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;

  @tracked isRevealed = false;

  private static readonly VISIBLE_CHARS = 4;
  private static readonly MAX_MASKED_CHARS = 16;

  get maskedSecret() {
    let { secret } = this.args;
    if (!secret) {
      return '';
    }

    let secretLength = secret.length;

    if (secretLength <= SensitiveDataDisplay.VISIBLE_CHARS) {
      return secret;
    }

    let maskLength = Math.min(
      SensitiveDataDisplay.MAX_MASKED_CHARS,
      secretLength - SensitiveDataDisplay.VISIBLE_CHARS,
    );
    let mask = '•'.repeat(maskLength);
    let visiblePart = secret.slice(-SensitiveDataDisplay.VISIBLE_CHARS);

    return mask + visiblePart;
  }

  get displayedSecret() {
    return this.isRevealed ? this.args.secret : this.maskedSecret;
  }

  @action
  async toggleReveal() {
    if (!this.isRevealed) {
      await this.logSecretActivity('revealed');
    }

    this.isRevealed = !this.isRevealed;
  }

  @action
  async onCopy() {
    await this.logSecretActivity('copied');
  }

  private async logSecretActivity(eventType: 'revealed' | 'copied') {
    try {
      await post(
        `/ember/developer_home/activity/secret_activity?app_id=${this.appService.app.id}`,
        {
          event_type: eventType,
          secret_type: this.args.secretType,
          app_package_id: this.args.appPackageId,
          label: this.args.label,
        },
      );
    } catch (error) {
      console.error('Error logging secret activity:', { eventType, error });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'DeveloperHub::AppPackage::SensitiveDataDisplay': typeof SensitiveDataDisplay;
    'developer-hub/app-package/sensitive-data-display': typeof SensitiveDataDisplay;
  }
}
